import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Users, Search, ArrowLeft, Eye, Calendar, Package, Activity } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import {
  fetchAllPreferences,
  fetchSessions,
  fetchUserPackages,
} from '../../lib/instantData'
import GlassCard from '../../components/ui/GlassCard'
import PanelShell from '../../components/ui/PanelShell'
import GradientButton from '../../components/ui/GradientButton'
import { useAuth } from '../../contexts/AuthContext'
import { isAdminEmail } from '../../lib/adminEmails'

interface Client {
  id: string
  email: string
  full_name?: string
  created_at: string
  avatar_url?: string
  last_login?: string
  phone?: string
  location?: string
  date_of_birth?: string
  fitness_goals?: string[]
  workout_time?: string
}

interface ClientStats {
  totalSessions: number
  completedSessions: number
  activePackages: number
}

const AdminUserDashboards: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [clientStats, setClientStats] = useState<Record<string, ClientStats>>({})
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchClients()
  }, [])

  const fetchClients = async () => {
    try {
      const preferences = await fetchAllPreferences()
      const clientsData = preferences
        .filter((p) => !isAdminEmail(p.email))
        .map((p) => ({
          id: p.id,
          email: p.email,
          full_name: p.full_name,
          created_at: p.created_at || '',
          avatar_url: p.avatar_url,
          last_login: p.last_login,
          phone: p.phone,
          location: p.location,
          date_of_birth: p.date_of_birth,
          fitness_goals: p.fitness_goals,
          workout_time: p.workout_time,
          user_id: p.user_id,
        }))
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))

      setClients(clientsData as any)
      for (const client of clientsData) {
        await fetchClientStats(client.email, client.user_id)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClientStats = async (clientEmail: string, userId: string) => {
    try {
      const [sessions, packages] = await Promise.all([
        fetchSessions({ userId }),
        fetchUserPackages(userId),
      ])
      const stats: ClientStats = {
        totalSessions: sessions.length,
        completedSessions: sessions.filter((s) => s.status === 'completed').length,
        activePackages: packages.filter((p) => p.status === 'active').length,
      }
      setClientStats((prev) => ({
        ...prev,
        [clientEmail]: stats,
      }))
    } catch (error) {
      console.error('Error fetching client stats:', error)
    }
  }

  const handleViewDashboard = (clientId: string) => {
    navigate(`/admin/user-dashboard/${clientId}`)
  }

  const filteredClients = clients.filter(client =>
    client.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gold"></div>
      </div>
    )
  }

  return (
    <PanelShell
      eyebrow="Admin"
      title="Client dashboards"
      subtitle="Open any client to edit sessions notes and recommendations."
      actions={
        <GradientButton
          onClick={() => navigate('/admin/dashboard')}
          variant="outline"
          size="sm"
          icon={ArrowLeft}
        >
          Admin home
        </GradientButton>
      }
    >
        <div className="mb-6">
          <GlassCard hover={false}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </GlassCard>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No clients found matching your search.' : 'No clients found.'}
              </p>
            </div>
          ) : (
            filteredClients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="h-full"
              >
                <GlassCard className="p-6 hover:shadow-lg transition-shadow h-full flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-display text-lg font-semibold text-ink mb-1">
                        {client.full_name || 'Unknown Client'}
                      </h3>
                                             <p className="text-sm text-gray-600 mb-2">
                         {client.email}
                       </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewDashboard(client.id)}
                        className="p-2 rounded-lg bg-ink hover:bg-ink-soft text-white transition-colors"
                        title="View Dashboard"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                                     {/* Client Stats */}
                   {clientStats[client.email] && (
                     <div className="space-y-3 flex-1">
                       <div className="flex items-center justify-between text-sm">
                         <span className="text-gray-600">Total Sessions:</span>
                         <span className="font-medium">{clientStats[client.email].totalSessions}</span>
                       </div>
                       <div className="flex items-center justify-between text-sm">
                         <span className="text-gray-600">Completed:</span>
                         <span className="font-medium text-brand">
                           {clientStats[client.email].completedSessions}
                         </span>
                       </div>
                       <div className="flex items-center justify-between text-sm">
                         <span className="text-gray-600">Active Packages:</span>
                         <span className="font-medium text-brand">
                           {clientStats[client.email].activePackages}
                         </span>
                       </div>
                     </div>
                   )}

                   {/* Quick Stats Icons */}
                   <div className="flex items-center justify-center space-x-4 mt-4 pt-4 border-t border-gray-100">
                     <div className="flex items-center text-xs text-gray-500">
                       <Calendar className="h-3 w-3 mr-1" />
                       {clientStats[client.email]?.totalSessions || 0}
                     </div>
                     <div className="flex items-center text-xs text-gray-500">
                       <Package className="h-3 w-3 mr-1" />
                       {clientStats[client.email]?.activePackages || 0}
                     </div>
                     <div className="flex items-center text-xs text-gray-500">
                       <Activity className="h-3 w-3 mr-1" />
                       {clientStats[client.email]?.completedSessions || 0}
                     </div>
                   </div>
                </GlassCard>
              </motion.div>
            ))
          )}
        </div>
    </PanelShell>
  )
}

export default AdminUserDashboards 